import { queryKeys, useHttpClient } from '@repo/api-client/react';
import {
  ClientSchema,
  type Client,
} from '@repo/api-client/dtos';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { putImage, type UploadableImage } from '../internal/imageUpload';

export interface UploadClientPhotoVariables {
  organizationId: string;
  clientId: string;
  file: UploadableImage;
}

/**
 * Multipart upload for a client's photo.
 *
 * Mirrors `useUploadOrgLogo` — same wire shape, same caching policy:
 * the server returns the updated client row, and we replace both the
 * detail cache and any matching list row so the new photo is visible
 * everywhere on the next render.
 */
export function useUploadClientPhoto() {
  const http = useHttpClient();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: ({ organizationId, clientId, file }: UploadClientPhotoVariables) =>
      putImage(
        http,
        `/orgs/${organizationId}/clients/${clientId}/photo`,
        file,
        ClientSchema,
      ),
    onSuccess: (client) => {
      queryClient.setQueryData(
        queryKeys.clients.detail(client.orgId, client.id),
        client,
      );
      // List cache is `InfiniteData<{ items, nextCursor? }>` since
      // the B1 (2026-05-09) migration to `useInfiniteQuery`.
      type ClientsPage = { items: Client[]; nextCursor?: string };
      type ClientsCache = { pages: ClientsPage[]; pageParams: unknown[] };
      queryClient.setQueryData<ClientsCache>(
        queryKeys.clients.all(client.orgId),
        (cache) =>
          cache
            ? {
                ...cache,
                pages: cache.pages.map((page) => ({
                  ...page,
                  items: page.items.map((c) =>
                    c.id === client.id ? { ...c, ...client } : c,
                  ),
                })),
              }
            : cache,
      );
    },
  });

  return {
    uploadPhoto: mutation.mutateAsync as (
      vars: UploadClientPhotoVariables,
    ) => Promise<Client>,
    loading: mutation.isPending,
    error: mutation.error,
  };
}
